import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Building2, MessageCircle } from "lucide-react";

const Avatar = ({ src, name, size = 40 }) => (
  src ? <img src={src} alt={name} style={{ width: size, height: size }} className="rounded-xl object-cover border border-slate-200" />
      : <div style={{ width: size, height: size }} className="rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><Building2 size={size * 0.5} /></div>
);

export default function ProNetworkMessages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // other business id
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [sp, setSp] = useSearchParams();
  const convRef = useRef(null);

  const loadThreads = () => api.get("/pro/network/messages/threads").then((r) => setThreads(r.data.threads || [])).catch(() => {});
  const openThread = (id) => {
    setActive(id);
    api.get(`/pro/network/messages/${id}`).then((r) => { setConv(r.data); loadThreads(); })
      .catch((e) => { if (e?.response?.status === 403) { toast.error("Vous devez être connectés pour échanger des messages."); setActive(null); setConv(null); } else { toast.error("Conversation indisponible"); } });
  };
  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { const to = sp.get("to"); if (to) { openThread(to); sp.delete("to"); setSp(sp, { replace: true }); } /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (convRef.current) convRef.current.scrollTop = convRef.current.scrollHeight; }, [conv]);

  const send = async () => {
    const t = text.trim(); if (!t || !active) return;
    setText("");
    try { await api.post("/pro/network/messages", { to_business_id: active, text: t }); openThread(active); }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };

  return (
    <div className="space-y-4" data-testid="pro-network-messages">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Messages</h1>
        <p className="text-slate-500 text-sm mt-1">Échangez avec vos partenaires du Réseau Pro local.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 bg-white border border-slate-200 rounded-2xl overflow-hidden" style={{ minHeight: 480 }}>
        {/* Liste des conversations */}
        <div className="border-r border-slate-100 overflow-y-auto" style={{ maxHeight: 560 }}>
          {threads.length === 0 ? (
            <div className="p-5 text-sm text-slate-400">Aucune conversation. Connectez-vous à des pros pour leur écrire.</div>
          ) : threads.map((t) => (
            <button key={t.business.id} onClick={() => openThread(t.business.id)} data-testid={`msg-thread-${t.business.id}`}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-slate-50 hover:bg-slate-50 ${active === t.business.id ? "bg-orange-50" : ""}`}>
              <Avatar src={t.business.logo} name={t.business.name} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate text-sm flex items-center gap-2">{t.business.name}
                  {t.unread > 0 && <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E05638] text-white text-[10px] font-bold grid place-items-center">{t.unread}</span>}
                </div>
                <div className="text-xs text-slate-400 truncate">{t.last || "Démarrer la conversation"}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Conversation active */}
        <div className="md:col-span-2 flex flex-col" style={{ maxHeight: 560 }}>
          {!conv ? (
            <div className="flex-1 grid place-items-center text-slate-400 text-sm p-8 text-center">
              <div><MessageCircle size={32} className="mx-auto mb-2 text-slate-300" />Sélectionnez une conversation</div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                <Avatar src={conv.other.logo} name={conv.other.name} size={34} />
                <div className="font-semibold text-slate-900">{conv.other.name}<span className="text-xs font-normal text-slate-400 ml-2">{conv.other.city}</span></div>
              </div>
              <div ref={convRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="msg-conversation">
                {conv.messages.map((m) => (
                  <div key={m.id} className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.from_id === conv.me_id ? "ml-auto bg-[#E05638] text-white" : "bg-slate-100 text-slate-800"}`}>
                    {m.text}
                    <div className={`text-[10px] mt-0.5 ${m.from_id === conv.me_id ? "text-white/70" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                ))}
                {conv.messages.length === 0 && <div className="text-center text-slate-400 text-sm py-6">Écrivez le premier message 👋</div>}
              </div>
              <div className="p-3 border-t border-slate-100 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }} placeholder="Votre message…" data-testid="msg-input" />
                <Button onClick={send} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="msg-send"><Send size={16} /></Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
